/// <reference types="vite/client" />

import { createInertiaApp, router } from '@inertiajs/react';
import { renderApp } from '@inertiaui/modal-react';
import * as Sentry from '@sentry/react';
import { createElement, type ReactElement } from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import ErrorBoundary, { ErrorFallback } from '../components/error-boundary';
import { setSentryUser, trackError } from '../lib/error-tracking';
import resolvePageLayout, { type InertiaPageModule } from '../lib/layout-resolver';
import type { CurrentUser } from '../types/user';

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;

if (!import.meta.env.SSR) {
  void import('../lib/theme').then(({ initTheme }) => {
    initTheme();
  });

  if (SENTRY_DSN) {
    Sentry.init({
      dsn: SENTRY_DSN,
      sendDefaultPii: false,
      tracesSampleRate: 0.1,
      integrations: [
        Sentry.replayIntegration({
          maskAllText: true,
          maskAllInputs: true,
          blockAllMedia: true,
        }),
      ],
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 1.0,
    });
  }

  router.on('invalid', (event) => {
    const response = event.detail.response;
    if (response.status === 422) {
      // Non-Inertia validation responses are expected; do not report them.
    } else {
      trackError(
        `Inertia visit returned a non-Inertia response (${response.status})`,
        new Error(`HTTP ${response.status} ${response.config?.url ?? ''}`)
      );
    }
  });

  router.on('exception', (event) => {
    trackError('Inertia visit exception', event.detail.exception);
  });

  router.on('navigate', (event) => {
    syncSentryUser(event.detail.page.props);
  });

  router.on('success', (event) => {
    syncSentryUser(event.detail.page.props);
  });
}

createInertiaApp({
  resolve: (name) => {
    const pages = import.meta.glob<InertiaPageModule>('../pages/**/*.tsx', { eager: true });
    const page = pages[`../pages/${name}.tsx`];

    if (!page) {
      const error = new Error(`Inertia page not found: ${name}`);
      trackError(`Inertia page not found: ${name}`, error);
      throw error;
    } else {
      return resolvePageLayout(name, page);
    }
  },

  setup({ el, App, props }) {
    syncSentryUser(props.initialPage.props);

    const appElement = wrapWithErrorBoundary(renderApp(App, props));

    if (el && el.hasChildNodes()) {
      hydrateRoot(el, appElement);
    } else {
      createRoot(el).render(appElement);
    }
  },
});

function syncSentryUser(props: { currentUser?: CurrentUser | null } | Record<string, unknown>) {
  const pageProps = props as { currentUser?: CurrentUser | null };
  setSentryUser(pageProps.currentUser);
}

function wrapWithErrorBoundary(appElement: ReactElement): ReactElement {
  if (SENTRY_DSN) {
    return createElement(
      Sentry.ErrorBoundary,
      { fallback: () => createElement(ErrorFallback) },
      appElement
    );
  } else {
    return createElement(ErrorBoundary, null, appElement);
  }
}
